import { toArgsArray, toArgsString } from 'app/model/adapters/activities/arguments-adapter.util';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';

import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { AzureStorageLinkedService } from '../../linkedservice/azure-storage.linkedservice';
import { HDInsightLinkedService } from '../../linkedservice/hdinsight.linkedservice';
import { ReferenceAdapter } from '../reference.adapter';
import { MapReduceActivity } from 'app/model/pipeline/map-reduce.activity';
import { toParameterClass, toParameterObject } from 'app/model/adapters/linkedservices/parameter-config.util';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import _ from 'lodash';
import { FilePath } from 'app/model/dataset/file-path';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(MapReduceActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        policy: '*',
        linkedServiceName: linkedServiceReferenceWithoutParameterizationRemainer,
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            jarFilePath: '*',
            jarLibs: '*',
            arguments: '*',
            getDebugInfo: '*',
            defines: '*',
            className: '*',
            jarLinkedService: linkedServiceReferenceWithoutParameterizationRemainer
        }
    }
})
export class MapReduceActivityAdapter extends ActivityAsyncAdapter<MapReduceActivity, Model.HDInsightMapReduceActivity> {

    public model(): IModel {
        return Metamodel.modelFor(MapReduceActivity);
    }

    protected async _toResource(activity: MapReduceActivity): Promise<Model.HDInsightMapReduceActivity> {
        const modelHDInsightMapReduceActivity: Model.HDInsightMapReduceActivity = super._getCommonActivityProperties(activity, 'HDInsightMapReduce') as Model.HDInsightMapReduceActivity;
        const mapReduceActivity: MapReduceActivity = activity as MapReduceActivity;
        const jarFilePath = ValueAdapter.toResource(mapReduceActivity, MapReduceActivity.JAR_FILE_PATH);

        modelHDInsightMapReduceActivity.typeProperties = {
            jarFilePath: jarFilePath.type ? jarFilePath : jarFilePath.path,
            arguments: toArgsArray(mapReduceActivity.mapReduceActivityArguments, mapReduceActivity, MapReduceActivity.ARGUMENTS),
            getDebugInfo: mapReduceActivity.getDebugInfo,
            className: ValueAdapter.toResource(mapReduceActivity, MapReduceActivity.CLASS_NAME),
            jarLibs: mapReduceActivity.jarLibs,
            defines: toParameterObject(mapReduceActivity.defines)
        };

        if (mapReduceActivity.jarLinkedService) {
            modelHDInsightMapReduceActivity.typeProperties.jarLinkedService = this._toLinkedServiceReferenceModel(mapReduceActivity.jarLinkedService);
        }

        if (mapReduceActivity.linkedService) {
            modelHDInsightMapReduceActivity.linkedServiceName = this._toLinkedServiceReferenceModel(mapReduceActivity.linkedService);
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            // Overlay this on http activity
            _.merge(modelHDInsightMapReduceActivity, parsed);
        }

        return modelHDInsightMapReduceActivity;
    }

    protected async _toModel(modelActivity: Model.HDInsightMapReduceActivity): Promise<MapReduceActivity> {
        const activity: MapReduceActivity = MapReduceActivity.MODEL.create<MapReduceActivity>(modelActivity.name);
        const modelMapReduceActivity = modelActivity as Model.HDInsightMapReduceActivity;

        this._updatedCommonProperties(modelActivity, activity);

        if (modelMapReduceActivity.typeProperties) {
            if (modelMapReduceActivity.typeProperties.jarFilePath && modelMapReduceActivity.typeProperties.jarFilePath.type) {
                ValueAdapter.toModel(activity, MapReduceActivity.JAR_FILE_PATH, modelMapReduceActivity.typeProperties.jarFilePath);
            } else {
                activity.jarFilePath = new FilePath();
                activity.jarFilePath.path = modelMapReduceActivity.typeProperties.jarFilePath || '';
            }
            const args = toArgsString(activity, modelMapReduceActivity.typeProperties.arguments, MapReduceActivity.ARGUMENTS);
            if (args) {
                activity.mapReduceActivityArguments = args;
            }
            ValueAdapter.toModel(activity, MapReduceActivity.CLASS_NAME, modelMapReduceActivity.typeProperties.className);
            activity.getDebugInfo = modelMapReduceActivity.typeProperties.getDebugInfo;
            activity.jarLibs = modelMapReduceActivity.typeProperties.jarLibs || [];
            activity.defines = toParameterClass(modelMapReduceActivity.typeProperties.defines, activity);

            if (modelMapReduceActivity.typeProperties.jarLinkedService !== undefined) {
                activity.jarLinkedService = ReferenceAdapter.toProxyModel<AzureStorageLinkedService>(Metamodel.modelFor(AzureStorageLinkedService),
                    modelMapReduceActivity.typeProperties.jarLinkedService, this.context);
            }
        }

        if (modelMapReduceActivity.linkedServiceName !== undefined) {
            activity.linkedService = ReferenceAdapter.toProxyModel<LinkedService>(Metamodel.modelFor(HDInsightLinkedService),
                modelMapReduceActivity.linkedServiceName, this.context);
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, MapReduceActivity.MODEL));

        return activity;
    }
}
